<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->


<template>
<div id="app" class="wrapper">
  <notifications position="top center" width="30%" :max="5">
    <template #body="props">
      <div class="notification vue-notification" :class="props.item.type">
        <button class="delete" @click="props.close"></button>
        <strong class="notification-title">
          {{props.item.title}}
        </strong>
        <div class="notification-content" v-html="props.item.text"></div>
      </div>
    </template>
  </notifications>

  <template v-if="!loading">
    <div class="box error" v-if="communicationError">
      <h2>
        {{$t('communication-error')}}
      </h2>
      {{$t('core-cannot-be-reached')}}
    </div>

    <template v-else-if="currentUser">
      <ims-navbar v-show="!topMenuHidden" />
      <div class="bottom">
        <keep-alive include="ims-storage">
          <router-view v-if="currentUser" />
        </keep-alive>
      </div>
      <!-- Zaya Chat (FR-30 -> chat-single-ui M2): a fixed-position overlay, mounted once here (like
           <notifications> above) so it floats above the router-view regardless of the current
           page. The island (@zaya-chat/ui-react) now owns the shell/positioning/store entirely —
           this wrapper just drives mount()/unmount() (FR-08, AC-3.1). chat-claim-gate: gated on
           `chatEnabled` (below) — v-if (not v-show) so a tenant without the `chat` module never
           even instantiates the component: no mount() call, no SDK/Centrifugo connection, no
           requests. -->
      <chat-island-mount v-if="chatEnabled" />
    </template>
  </template>
</div>
</template>

<script>
import axios from 'axios';
import ifvisible from 'ifvisible';
ifvisible.setIdleDuration(constants.IDLE_DURATION);

import {Ims} from '@/api';
import constants from '@/utils/constants.js';
import {get} from '@/utils/store-helpers';
import {updateToken} from '@/utils/token-utils';
import {isVRBrowser, startGamepadWatch} from '@/utils/vr';
import {changeLanguageMixin} from '@/lang.js';
import {isChatEnabled} from '@/chat-island/chatCapability';

import ImsNavbar from '@/components/navbar/ImsNavbar.vue';
import ChatIslandMount from '@/chat-island/ChatIslandMount.vue';

export default {
  name: 'app',
  components: {
    ImsNavbar,
    ChatIslandMount,
  },
  mixins: [
    changeLanguageMixin,
  ],
  data() {
    return {
      communicationError: false,
      loading: true,
      timeout: null,
      topMenuHidden: false, // UI-5: viewer can hide the top navbar for more canvas
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    project: get('currentProject/project'),
    // chat-claim-gate: recomputes whenever `currentUser` changes reference (login/logout/refresh
    // are the only times the Keycloak token — and therefore its `zaya_modules` claim — actually
    // changes), so this stays live without its own reactive store. See chatCapability.js.
    chatEnabled() {
      // eslint-disable-next-line no-unused-expressions
      this.currentUser;
      return isChatEnabled();
    }
  },
  watch: {
    $route() {
      // Invoke refresh token if needed when route changes.
      updateToken();
      // UI-5: always restore the top menu when leaving the current (viewer) screen.
      this.topMenuHidden = false;
    },
  },
  mounted() {
    this.$eventBus.$on('viewer-toggle-top-menu', this.setTopMenuHidden);
    // VR-only: tag the document so headset-specific styles apply, and watch the controller face
    // buttons so a button (e.g. Y) switches the active viewer to the Select tool. Laptop untouched.
    if (isVRBrowser()) {
      document.documentElement.classList.add('vr-mode');
      this._stopGamepad = startGamepadWatch(() => {
        this.$eventBus.$emit('vr-shortcut-select');
      });
    }
    // Web push wiring (notification-triggers.spec.md R9) moved into `<chat-island-mount>` itself
    // (chat-single-ui M2) — it now owns the ChatHost values push needs (chatBaseUrl/getAccessToken)
    // and the package-level `openConversation(el, id)` push-click handler needs.
  },
  beforeDestroy() {
    this.$eventBus.$off('viewer-toggle-top-menu', this.setTopMenuHidden);
    if (this._stopGamepad) {
      this._stopGamepad();
    }
  },
  methods: {
    setTopMenuHidden(hidden) {
      this.topMenuHidden = hidden;
    },
    wakeup: async function () {
      if (!ifvisible.now()) {
        return;
      }
      // Guard 57. This is the first refresh after a sleep/background period, and the network
      // is often not back yet - a rejection here must not skip ping() (which is what surfaces
      // the communication banner) or bubble as an unhandled rejection. A dead SSO session
      // (refresh -> 400) is keycloak-js's job: clearToken() + login() redirect.
      try {
        await updateToken();
      } catch (e) {
        console.warn('token refresh on wakeup failed', e);
      }
      await this.ping();
    },
    async ping() {
      if (!ifvisible.now()) {
        return; // window not visible or inactive user => stop pinging
      }
      try {
        // TODO IAM - still needed ?
        // await Ims.instance.ping(this.project ? this.project.id : null);
        if (!this.currentUser) {
          await this.fetchUser();
        }
        this.communicationError = false;
      } catch (error) {
        console.log(error);
        this.communicationError = error.toString().indexOf('401') === -1;
      }

      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.ping, constants.PING_INTERVAL);
    },
    async fetchUser() {
      await this.$store.dispatch('currentUser/fetchUser');
      if (this.currentAccount) {
        this.changeLanguage(this.currentAccount.locale);
      }
    },
    shouldAttachTenantHeader(config) {
      const activeTenant = (constants.ACTIVE_TENANT || '').toString().trim().toLowerCase();
      if (!activeTenant) {
        return false;
      }

      const normalizeBase = value => (value || '').toString().trim().replace(/\/+$/, '');
      const requestBase = normalizeBase(config?.baseURL);
      const coreBase = normalizeBase(constants.CORE_URL);
      if (!requestBase || !coreBase) {
        return false;
      }
      return requestBase === coreBase;
    }
  },
  async created() {
    let settings;
    await axios
      .get('configuration.json')
      .then(response => (settings = response.data));

    for (let i in settings) {
      if (Object.prototype.hasOwnProperty.call(constants, i)
        || i.includes('_NAMESPACE') || i.includes('_VERSION') || i.includes('_ENABLED')) {
        constants[i] = settings[i];
      }
    }
    Object.freeze(constants);

    const authorizationHeaderInterceptor = async config => {
      // Guard 57. A refresh failure must not kill the request client-side: if Keycloak is
      // briefly unreachable (laptop wake, IdP restart) the CURRENT access token can still be
      // valid for many minutes, and the server is the authority on whether it is - fall back
      // to it and let the request succeed or 401 truthfully. A refresh rejected with 400
      // (SSO session dead) is handled by keycloak-js itself: clearToken() + login() redirect
      // (we init with onLoad 'login-required'), so no session-expiry case is masked here.
      let token;
      try {
        token = await updateToken();
      } catch (e) {
        token = this.$keycloak.token || null;
      }
      const activeTenant = (constants.ACTIVE_TENANT || '').toString().trim().toLowerCase();

      config.headers = config.headers || {};

      if (token !== null) {
        this.$store.commit('currentUser/setShortTermToken', token);
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      if (activeTenant && this.shouldAttachTenantHeader(config)) {
        config.headers['X-TENANT'] = activeTenant;
      } else if (config.headers['X-TENANT']) {
        delete config.headers['X-TENANT'];
      }
      return config;
    };
    new Ims(
      constants.CORE_URL,
      constants.IAM_URL,
      authorizationHeaderInterceptor
    );

    await this.ping();
    this.loading = false;
    // Chat (chat-single-ui M2): no explicit init call needed — <chat-island-mount> (rendered only
    // once `currentUser` is set, see the template above) drives its own mount()/unmount() and
    // builds its own SDK client from the IMS ChatHost adapter (FR-05).
    ifvisible.on('wakeup', this.wakeup);
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/main.scss';

@font-face {
  font-family: 'ims';
  src: url('assets/ims-font.woff') format('woff');
}

/* VR headset only (Meta Quest browser): enlarge the viewer tool buttons so they are easy to hit
   with a shaky controller laser pointer. Gated by html.vr-mode → laptop is never affected. */
html.vr-mode .canvas-toolbar button,
html.vr-mode .canvas-toolbar .button {
  min-width: 3rem;
  min-height: 3rem;
  font-size: 1.15rem;
}
html.vr-mode .canvas-toolbar .ct-grid {
  gap: 0.45rem;
}

html, body {
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

body {
  font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif, sans-serif;
  color: #333;
  background: $page-bg;
}

.wrapper {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  background: $page-bg;
}

.box.error {
  max-width: 600px;
  margin: auto;
  margin-top: 3rem;
}

.notifications {
  margin-top: 1em;
}

.notification.info {
  background: #77b1ea;
}

.bottom {
  flex: 1;
  overflow-y: auto;
  /* position: relative; */
}

h1 {
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  font-size: 1.25rem;
  text-align: center;
  padding: 0.9rem;
}

h2 {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 1rem;
}

.in-project {
  color: grey;
  font-size: 0.8em;
}

.input[readonly] {
  /* UI-3d: white theme — readonly inputs were grey "whitesmoke"; keep them white,
     the disabled cue comes from the muted text/border. */
  background-color: #fff;
}

strong, .label {
  font-weight: 600 !important;
}

.content-wrapper {
  padding: 1.5% 2.5%;
  position: relative;
  min-height: 100%;
}

/* Filters */

.filters {
  /* UI-3d: white theme — was grey #f8f8f8; white surface with a hairline border. */
  background: #fff;
  border: 1px solid #e5e8ee;
  margin-top: 1.2rem;
  border-radius: 10px;
  padding: 1rem;
}

.filter-label {
  text-transform: uppercase;
  font-size: 0.8em;
  margin-bottom: 0.5em;
  margin-left: 1em;
}

.filter-label .no-uppercase {
  text-transform: none;
}

.nb-active-filters {
  display: inline-block;
  background: $primary;
  color: $primary-invert;
  min-width: 1.25rem;
  height: 1.25rem;
  font-weight: 600;
  border-radius: 0.625rem;
  margin-left: 0.5em;
  font-size: 0.9em;
  line-height: 1.25em;
  padding: 0 0.25em;
  position: absolute;
  top: -0.3em;
  right: -0.6em;
}

/* For correct display of svg images on IE > 10 */
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  img[src$=".svg"] {
    width: 100%;
  }
}
</style>
